import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SiteSystemType } from "api/ApiClient";
import { apiClientFactory } from "helpers";
import { Trash2 } from "lucide-react";
import { Alert } from "components/ui/alert";
import { AdminTable } from "components/shared/AdminTable";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import AddLockToGroupModal from "./components/AddLockToGroupModal";
import CloudManagedWarningDialog from "./components/CloudManagedWarningDialog";

type Props = {
  groupId: string;
  groupSiteId?: string;
  cloudManagedSystemType?: SiteSystemType.Brivo | SiteSystemType.Openpath;
};

export default function GroupLocks({
  groupId,
  groupSiteId,
  cloudManagedSystemType,
}: Props) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingDeleteLockId, setPendingDeleteLockId] = React.useState<string | null>(null);
  const [showCloudWarning, setShowCloudWarning] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["acg", "locks", groupId],
    queryFn: () => apiClientFactory().getLocksForAccessControlGroup(groupId, 0, 2000, undefined),
  });

  const locks = React.useMemo(
    () =>
      (data ?? [])
        .map((item) => item.lock)
        .filter((lock): lock is NonNullable<typeof lock> => !!lock),
    [data],
  );

  const removeMutation = useMutation({
    mutationFn: async (lockId: string) => {
      await apiClientFactory().deleteLockFromAccessControlGroup(groupId, lockId);
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["acg", "locks", groupId] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message || e?.message || "Unable to remove lock.");
    },
  });

  const assignedLockIds = locks.map((lock) => lock.id as string).filter(Boolean);

  return (
    <div>
      {error ? <Alert className="mb-2" variant="destructive">{error}</Alert> : null}

      {isLoading ? (
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading locks...
        </div>
      ) : (
        <AdminTable
          aria-label="Group locks"
          toolbar={
            <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                Locks currently included in this access control group.
              </p>
              <Button onClick={() => setAddOpen(true)} size="sm" type="button">
                Add Locks
              </Button>
            </div>
          }
        >
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Site</th>
              <th scope="col">Door Type</th>
              <th scope="col">System Lock Id</th>
              <th scope="col" className="w-20 text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {locks.map((lock) => (
              <tr key={lock.id}>
                <td>
                  <Link to={`/admin/locks/${lock.id}`}>{lock.name}</Link>
                </td>
                <td>{lock.site?.name || lock.siteId}</td>
                <td>
                  <Badge variant="outline">{lock.doorType || "Unknown"}</Badge>
                </td>
                <td className="font-mono text-xs">{lock.systemLockId}</td>
                <td className="text-right">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="Remove lock from group"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={removeMutation.isPending}
                        onClick={() => {
                          if (!lock.id) {
                            return;
                          }

                          if (cloudManagedSystemType) {
                            setPendingDeleteLockId(lock.id);
                            setShowCloudWarning(true);
                            return;
                          }

                          removeMutation.mutate(lock.id);
                        }}
                        size="icon-sm"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Remove lock from group</TooltipContent>
                  </Tooltip>
                </td>
              </tr>
            ))}
            {locks.length === 0 && (
              <tr>
                <td colSpan={5} className="py-16 text-sm text-center text-slate-500">
                  No locks assigned to this group.
                </td>
              </tr>
            )}
          </tbody>
        </AdminTable>
      )}

      <AddLockToGroupModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={async () => {
          await queryClient.invalidateQueries({ queryKey: ["acg", "locks", groupId] });
        }}
        groupId={groupId}
        groupSiteId={groupSiteId}
        cloudManagedSystemType={cloudManagedSystemType}
        assignedLockIds={assignedLockIds}
      />

      {cloudManagedSystemType ? (
        <CloudManagedWarningDialog
          open={showCloudWarning}
          systemType={cloudManagedSystemType}
          busy={removeMutation.isPending}
          onCancel={() => {
            setShowCloudWarning(false);
            setPendingDeleteLockId(null);
          }}
          onConfirm={() => {
            if (!pendingDeleteLockId) {
              return;
            }
            removeMutation.mutate(pendingDeleteLockId);
            setShowCloudWarning(false);
            setPendingDeleteLockId(null);
          }}
        />
      ) : null}
    </div>
  );
}
